/**
 * Authentication Utilities
 * Helper functions for working with auth state and tokens.
 */
import { supabase } from '../supabaseClient';

/**
 * Check if the user is currently logged in
 * @returns {Promise<boolean>} True if logged in, false otherwise
 */
export const isLoggedIn = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return !!session;
};

/**
 * Get the current user's session
 * @returns {Promise<Object|null>} Session object or null if not logged in
 */
export const getSession = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return session;
};

/**
 * Get the current auth token
 * @returns {Promise<string|null>} Access token or null if not logged in
 */
export const getAccessToken = async () => {
  const session = await getSession();
  return session?.access_token || null;
};

/**
 * Get the current user's ID
 * @returns {Promise<string|null>} User ID or null if not logged in
 */
export const getUserId = async () => {
  const session = await getSession();
  return session?.user?.id || null;
};

/**
 * Refreshes the session if needed
 * @returns {Promise<boolean>} True if refreshed successfully or not needed
 */
export const refreshSessionIfNeeded = async () => {
  const session = await getSession();
  
  if (!session) return false;
  
  // Check if token is about to expire (within 5 minutes)
  const tokenExpiryTime = session.expires_at * 1000; // convert to milliseconds
  const currentTime = Date.now();
  const fiveMinutes = 5 * 60 * 1000;
  
  if (tokenExpiryTime - currentTime < fiveMinutes) {
    const { data, error } = await supabase.auth.refreshSession();
    return !error && !!data.session;
  }
  
  return true;
};

/**
 * Sign out the current user
 * @returns {Promise<boolean>} True if signed out successfully
 */
export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  return !error;
};

/**
 * Check if we're using a local Supabase instance
 * @returns {boolean} True if using local Supabase
 */
export const isUsingLocalSupabase = () => {
  return process.env.NODE_ENV === 'development' && 
         window.location.hostname === 'localhost' &&
         supabase.supabaseUrl.includes('127.0.0.1');
};

/**
 * Debug info about the current authentication state
 * @returns {Promise<Object>} Debug information
 */
export const getAuthDebugInfo = async () => {
  const session = await getSession();
  
  return {
    isLoggedIn: !!session,
    userId: session?.user?.id || null,
    email: session?.user?.email || null,
    tokenExpiry: session?.expires_at ? new Date(session.expires_at * 1000).toISOString() : null,
    isUsingLocalSupabase: isUsingLocalSupabase(),
    supabaseUrl: supabase.supabaseUrl
  };
};